import type {
  DeficiencyDetailsResponse,
  DeficiencyDocument,
  DeficiencyTaskDecision,
} from "@services/deficiencies/types";
import type { TFunction } from "i18next";

type BadgeType = "success" | "warning" | "error";
const LANG_BASE = "deficiencies.deficiency-details";

export const mapDeficiencyStatus = (
  status?: string,
  t?: TFunction,
): { label: string; type: BadgeType } => {
  if (!status) {
    return { label: "-", type: "warning" };
  }

  const STATUS_CONFIG: Record<string, { type: BadgeType }> = {
    PENDING_RESOLUTION: { type: "warning" },
    PENDING_APPROVAL: { type: "warning" },
    AWAITING_APPROVAL: { type: "warning" },
    RESOLVED: { type: "success" },
  };

  return {
    label: t?.(`deficiencies.status-labels.${status}`) || status,
    type: STATUS_CONFIG[status]?.type || "warning",
  };
};

export const formatDeficiencyDateTime = (value?: string | null): string => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export function getFileExtensionFromUrl(url?: string | null): string {
  if (!url) return "";
  const cleanUrl = url.split("?")[0].split("#")[0];
  const lastDotIndex = cleanUrl.lastIndexOf(".");
  if (lastDotIndex === -1) return "";
  return cleanUrl.substring(lastDotIndex + 1);
}

const hasDocumentUrl = (document?: DeficiencyDocument | null) =>
  typeof document?.documentUrl === "string" && document.documentUrl.trim().length > 0;

export const getApprovalTaskDecisionAttachments = (
  taskDecision?: DeficiencyTaskDecision | null,
): DeficiencyDocument[] => {
  const attachments = taskDecision?.approvalDocuments ?? [];

  return attachments.filter(hasDocumentUrl);
};

export const getRejectionTaskDecisionAttachments = (
  taskDecision?: DeficiencyTaskDecision | null,
): DeficiencyDocument[] => {
  const attachments = taskDecision?.rejectionDocuments ?? [];

  return attachments.filter(hasDocumentUrl);
};

export const hasDeficiencyTaskDecisionDetails = (
  taskDecision?: DeficiencyTaskDecision | null,
): boolean => {
  if (!taskDecision) {
    return false;
  }

  return Boolean(
    taskDecision.approvalNotes ||
      getApprovalTaskDecisionAttachments(taskDecision).length > 0 ||
      taskDecision.rejectionNotes ||
      getRejectionTaskDecisionAttachments(taskDecision).length > 0,
  );
};

export const canViewDeficiencyTaskDecisionDetails = (
  taskDecision?: DeficiencyTaskDecision | null,
): boolean => {
  return (
    Boolean(taskDecision) &&
    (taskDecision?.status === "APPROVED" || taskDecision?.status === "REJECTED") &&
    hasDeficiencyTaskDecisionDetails(taskDecision)
  );
};

export function getDeficiencyDetailsItems({
  details,
  TEST_ID_BASE,
  t,
}: {
  details?: DeficiencyDetailsResponse | null;
  TEST_ID_BASE: string;
  t: TFunction;
}) {
  const leftColumn = [
    {
      testId: `${TEST_ID_BASE}-vessel-name`,
      label: t(`${LANG_BASE}.vessel-name`),
      value: details?.vesselName || "-",
    },
    {
      testId: `${TEST_ID_BASE}-trip-no-plda`,
      label: t(`${LANG_BASE}.trip-no-plda`),
      value: details?.tripNoPlda || "-",
    },
    {
      testId: `${TEST_ID_BASE}-loa`,
      label: t(`${LANG_BASE}.loa`),
      value: details?.loa || "-",
    },
  ];

  const rightColumn = [
    {
      testId: `${TEST_ID_BASE}-imo`,
      label: t(`${LANG_BASE}.imo`),
      value: details?.vesselImoNumber || "-",
    },
    {
      testId: `${TEST_ID_BASE}-berth-visited`,
      label: t(`${LANG_BASE}.berth-visited`),
      value: details?.berthVisited || "-",
    },
    {
      testId: `${TEST_ID_BASE}-date-time-of-deficiency`,
      label: t(`${LANG_BASE}.date-time-of-deficiency`),
      value: formatDeficiencyDateTime(details?.createdAt),
    },
  ];

  return {
    leftColumn,
    rightColumn,
    detailsText: details?.details || "-",
    remarksText: details?.remarks || "-",
  };
}
